import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DatabaseBackup, Download } from 'lucide-react';
import type { TenantExportDto, TenantExportLinkDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

const DATA_EXPORT_KEY = ['data-export'] as const;

function statusText(row: TenantExportDto): string {
  switch (row.status) {
    case 'queued':
      return row.notBefore ? `Queued — ready after ${dateTime(row.notBefore)} (your time); we will email you` : 'Queued — starting shortly';
    case 'running':
      return 'Being prepared';
    case 'ready':
      return `Ready — download once before ${row.expiresAt ? dateTime(row.expiresAt) : 'it expires'}`;
    case 'downloaded':
      return `Downloaded ${row.downloadedAt ? dateTime(row.downloadedAt) : ''}`;
    case 'failed':
      return `Could not be prepared: ${row.error ?? 'unknown reason'}`;
    default:
      return row.status;
  }
}

export function DataExportCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const exports = useQuery({ queryKey: DATA_EXPORT_KEY, queryFn: () => get<TenantExportDto[]>('/data-export'), staleTime: 60_000, retry: false });
  const request = useMutation({
    mutationFn: () => post<TenantExportDto>('/data-export', { totpCode: code }),
    onSuccess: () => {
      setCode('');
      toast.success(tr('admin', 'download_requested'));
      void queryClient.invalidateQueries({ queryKey: DATA_EXPORT_KEY });
    },
    onError: (err) => toast.error(tr('admin', 'could_not_request_the_download'), describeError(err)),
  });
  const link = useMutation({
    mutationFn: (id: string) => post<TenantExportLinkDto>(`/data-export/${id}/link`),
    onSuccess: (result) => {
      window.location.assign(result.url);
      void queryClient.invalidateQueries({ queryKey: DATA_EXPORT_KEY });
    },
    onError: (err) => toast.error(tr('admin', 'could_not_start_the_download'), describeError(err)),
  });
  const busy = (exports.data ?? []).some((row) => row.status === 'queued' || row.status === 'running');

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <DatabaseBackup className="h-4 w-4 text-primary" aria-hidden /> {tr('admin', 'download_all_data')}
        </CardTitle>
        <CardDescription>
          {tr('admin', 'a_copy_of_every_record_and_stored_file_of_this')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {(exports.data ?? []).slice(0, 5).map((row) => (
          <div key={row.id} className="flex flex-wrap items-center gap-2">
            <StatusBadge status={row.status} tone={row.status === 'ready' ? 'success' : row.status === 'failed' ? 'destructive' : 'muted'} />
            <span className="text-xs text-muted-foreground">{statusText(row)}</span>
            {row.status === 'ready' && (
              <Button type="button" size="sm" variant="outline" loading={link.isPending} onClick={() => link.mutate(row.id)}>
                <Download className="h-3.5 w-3.5" /> {tr('admin', 'download')}
              </Button>
            )}
          </div>
        ))}
        {!busy && (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder={tr('admin', 'your_6_digit_authenticator_code')}
              className="w-60"
              aria-label={tr('admin', 'authenticator_code')}
            />
            <Button type="button" variant="outline" loading={request.isPending} disabled={code.length !== 6} onClick={() => request.mutate()}>
              {tr('admin', 'request_a_full_download')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
